
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Clock, DollarSign, Star, Users, Zap } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const Games = () => {
  const games = {
    powerball: {
      name: 'PowerBall',
      description: 'Australia\'s premier lottery with the biggest jackpots',
      jackpot: '$5,000,000',
      nextDraw: 'Thursday 8:00 PM AEDT',
      howToPlay: [
        'Choose 7 numbers from 1 to 35',
        'Choose 1 PowerBall number from 1 to 20',
        'Match all numbers to win Division 1',
        'Multiple ways to win across 9 divisions'
      ],
      odds: '1 in 134,490,400',
      minBet: '$5',
      draws: 'Thursday nights',
      divisions: [
        { division: 1, match: '7 + PowerBall', odds: '1 in 134,490,400', prize: 'Jackpot' },
        { division: 2, match: '7', odds: '1 in 7,078,443', prize: '$150,000+' },
        { division: 3, match: '6 + PowerBall', odds: '1 in 686,176', prize: '$10,000+' },
        { division: 4, match: '6', odds: '1 in 36,115', prize: '$500+' },
        { division: 5, match: '5 + PowerBall', odds: '1 in 16,943', prize: '$200+' },
        { division: 6, match: '4 + PowerBall', odds: '1 in 1,173', prize: '$65+' },
        { division: 7, match: '5', odds: '1 in 892', prize: '$45+' },
        { division: 8, match: '3 + PowerBall', odds: '1 in 188', prize: '$20+' },
        { division: 9, match: '2 + PowerBall', odds: '1 in 66', prize: '$10+' }
      ]
    },
    ozlotto: {
      name: 'Oz Lotto',
      description: 'Tuesday night\'s big jackpot lottery',
      jackpot: '$8,000,000',
      nextDraw: 'Tuesday 8:00 PM AEDT',
      howToPlay: [
        'Choose 7 numbers from 1 to 45',
        'Match all 7 numbers to win Division 1',
        'Plus 2 supplementary numbers drawn',
        'Seven prize divisions to win'
      ],
      odds: '1 in 45,379,620',
      minBet: '$4',
      draws: 'Tuesday nights',
      divisions: [
        { division: 1, match: '7 numbers', odds: '1 in 45,379,620', prize: 'Jackpot' },
        { division: 2, match: '6 + 1 or 2 supp', odds: '1 in 3,241,401', prize: '$50,000+' },
        { division: 3, match: '6 numbers', odds: '1 in 180,078', prize: '$5,000+' },
        { division: 4, match: '5 + 1 or 2 supp', odds: '1 in 29,602', prize: '$500+' },
        { division: 5, match: '5 numbers', odds: '1 in 3,430', prize: '$100+' },
        { division: 6, match: '4 numbers', odds: '1 in 154', prize: '$40+' },
        { division: 7, match: '3 + 1 or 2 supp', odds: '1 in 87', prize: '$20+' }
      ]
    },
    saturday: {
      name: 'Saturday Lotto',
      description: 'The classic Australian lottery game',
      jackpot: '$3,000,000',
      nextDraw: 'Saturday 8:00 PM AEDT',
      howToPlay: [
        'Choose 6 numbers from 1 to 45',
        'Match all 6 numbers to win Division 1',
        'Plus 2 supplementary numbers drawn',
        'Six prize divisions available'
      ],
      odds: '1 in 8,145,060',
      minBet: '$3',
      draws: 'Saturday nights',
      divisions: [
        { division: 1, match: '6 numbers', odds: '1 in 8,145,060', prize: 'Jackpot' },
        { division: 2, match: '5 + 1 or 2 supp', odds: '1 in 678,755', prize: '$25,000+' },
        { division: 3, match: '5 numbers', odds: '1 in 36,689', prize: '$1,500+' },
        { division: 4, match: '4 numbers', odds: '1 in 733', prize: '$50+' },
        { division: 5, match: '3 + 1 or 2 supp', odds: '1 in 297', prize: '$25+' },
        { division: 6, match: '1 or 2 + 2 supp', odds: '1 in 144', prize: '$15+' }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Lottery Games</h1>
          <p className="text-xl text-gray-700">Discover your perfect game and learn how to play</p>
        </div>
      </section>

      {/* Games Overview */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {Object.entries(games).map(([key, game]) => (
              <Card key={key} className="hover:shadow-lg transition-shadow">
                <CardHeader className="text-center">
                  <Trophy className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                  <CardTitle className="text-2xl text-yellow-600">{game.name}</CardTitle>
                  <p className="text-gray-600">{game.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{game.jackpot}</p>
                    <p className="text-gray-600">Current Jackpot</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Next Draw</p>
                      <p className="font-medium">{game.nextDraw}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Min Bet</p>
                      <p className="font-medium">{game.minBet}</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                    onClick={() => window.location.href = '/play'}
                  >
                    Play {game.name}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="powerball" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="powerball">PowerBall</TabsTrigger>
              <TabsTrigger value="ozlotto">Oz Lotto</TabsTrigger>
              <TabsTrigger value="saturday">Saturday Lotto</TabsTrigger>
            </TabsList>
            
            {Object.entries(games).map(([key, game]) => (
              <TabsContent key={key} value={key}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Star className="w-6 h-6 text-yellow-600" />
                        How to Play {game.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {game.howToPlay.map((step, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-yellow-600 rounded-full flex items-center justify-center text-white text-sm font-bold mt-0.5">
                              {index + 1}
                            </div>
                            <span className="text-gray-700">{step}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Jackpot Odds</p>
                          <p className="font-bold">{game.odds}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Draw Night</p>
                          <p className="font-bold">{game.draws}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-6 h-6 text-yellow-600" />
                        Prize Divisions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {game.divisions.map((division) => (
                          <div key={division.division} className="flex justify-between items-center p-3 border rounded-lg">
                            <div>
                              <p className="font-medium">Div {division.division}</p>
                              <p className="text-sm text-gray-600">{division.match}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{division.prize}</p>
                              <p className="text-xs text-gray-600">{division.odds}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Playing Tips & Strategies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-6">
                <Users className="w-10 h-10 text-yellow-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Play in Groups</h3>
                <p className="text-gray-700">Join a syndicate to increase your chances and share the cost with friends or colleagues.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <Clock className="w-10 h-10 text-yellow-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Regular Play</h3>
                <p className="text-gray-700">Consistent play with the same numbers can be a valid strategy for long-term players.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <Zap className="w-10 h-10 text-yellow-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Quick Pick</h3>
                <p className="text-gray-700">Let the computer choose random numbers - many jackpot winners used Quick Pick!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Games;
